import { lazy, Suspense, useEffect, useState } from "react";
import { MotionConfig } from "framer-motion";
import "bootstrap/dist/css/bootstrap.min.css";
import "./App.css";
import "./styles/futuristic.css";
import { NavBar } from "./components/NavBar";
import { Banner } from "./components/Banner";
import { ShootingStars } from "./components/ShootingStars";
import { GlassFilter } from "./components/LiquidGlass";
import { RGBWaveShader } from "./components/RGBWaveShader";

// Below-fold sections loaded only when needed
const About      = lazy(() => import("./components/About").then(m => ({ default: m.About })));
const Experience = lazy(() => import("./components/Experience").then(m => ({ default: m.Experience })));
const Projects   = lazy(() => import("./components/Projects").then(m => ({ default: m.Projects })));
const Skills     = lazy(() => import("./components/Skills").then(m => ({ default: m.Skills })));
const Leadership = lazy(() => import("./components/Leadership").then(m => ({ default: m.Leadership })));
const AISection  = lazy(() => import("./components/AISection").then(m => ({ default: m.AISection })));
const CosmicFooter = lazy(() => import("./components/CosmicFooter").then(m => ({ default: m.CosmicFooter })));

// Reserve space while below-fold chunks load so the page never jumps.
const SectionFallback = ({ height = 640 }) => (
  <div className="section-fallback" style={{ minHeight: height }} aria-hidden="true" />
);

function App() {
  const [cursor, setCursor] = useState({ x: 0, y: 0, active: false });

  useEffect(() => {
    const onMove = (e) => setCursor({ x: e.clientX, y: e.clientY, active: true });
    const onLeave = () => setCursor((prev) => ({ ...prev, active: false }));
    const onScroll = () => {
      document.documentElement.style.setProperty("--scroll-y", `${window.scrollY}`);
    };

    onScroll();
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseleave", onLeave);
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseleave", onLeave);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  return (
    <MotionConfig reducedMotion="user">
    <div className="App">
      <GlassFilter />
      <ShootingStars />

      <div className="glitch-overlay" aria-hidden="true" />

      <div
        className={`moon-cursor ${cursor.active ? "is-visible" : ""}`}
        style={{ transform: `translate3d(${cursor.x}px, ${cursor.y}px, 0)` }}
        aria-hidden="true"
      />

      <div className="site-content">
        <NavBar />
        <Banner />
        <Suspense fallback={<SectionFallback height={640} />}><About /></Suspense>
        <Suspense fallback={<SectionFallback height={900} />}><Experience /></Suspense>
        <Suspense fallback={<SectionFallback height={1100} />}>
          <div className="wave-bg-section">
            <RGBWaveShader />
            <Projects />
          </div>
        </Suspense>
        <Suspense fallback={<SectionFallback height={1200} />}><Skills /></Suspense>
        <Suspense fallback={<SectionFallback height={420} />}><Leadership /></Suspense>
        <Suspense fallback={<SectionFallback height={640} />}><AISection /></Suspense>
        <Suspense fallback={<SectionFallback height={700} />}><CosmicFooter /></Suspense>
      </div>
    </div>
    </MotionConfig>
  );
}

export default App;
